/**
 * The Fund Operations Console.
 *
 * A standalone application with its own sign-in (IP-72): the operator signs in
 * with their email, password and authenticator code against the Core's own
 * user pool, through the Console API - the browser never calls Cognito. What
 * they may then see is their Core claims (auth/AuthGate.jsx,
 * @lfoc/domain/permissions.js), and the server checks every one of them again
 * on every request.
 *
 * src/main.js mounts it, loaded by the page the build writes (src/index.html);
 * React, antd and the rest are bundled rather than fetched, and every script
 * carries a Subresource Integrity hash, so nothing runs that the build did not
 * fingerprint (rspack.config.mjs).
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConfigProvider } from 'antd';

import Console from './Console.jsx';
import { paletteCss } from './config/palette.js';
import { ANTD_PREFIX, antdTheme } from './config/theme.js';
import { createConsoleStore } from './redux/store.js';
import styles from './styles/console.css';
import registryStyles from './styles/registry.css';

/*
 * antd's static APIs render into their own React root and would otherwise fall
 * back to the default prefix and blue theme. The screens use App.useApp(), so
 * this is a backstop rather than the path anything takes.
 */
ConfigProvider.config({ prefixCls: ANTD_PREFIX, theme: antdTheme });

/**
 * The Console owns exactly one style element, so unmount can take it back out:
 * Lynear's palette as CSS variables (config/palette.js - the one place a
 * colour is set), console.css, then the live registry screens' registry.css
 * (IP-112), all scoped under .lfoc-styles-wrapper.
 */
function injectStyles() {
  let el = document.head.querySelector('style[data-lfoc]');
  if (!el) {
    el = document.createElement('style');
    el.dataset.lfoc = '';
    el.textContent = paletteCss() + '\n' + styles + '\n' + registryStyles;
    document.head.append(el);
  }
  return el;
}

/**
 * Mounted by src/main.js when the page loads.
 *
 * @param {HTMLElement} root
 * @param {{ apiBase: string }} context  apiBase - the Console API, '/api' on the Console's own origin
 */
export function mount(root, context) {
  if (!root) throw new Error('the Console needs a mount point');
  if (!context?.apiBase) throw new Error('the Console needs the Console API base URL');
  const config = { apiBase: context.apiBase };

  root.textContent = '';
  const styleEl = injectStyles();
  const reactRoot = createRoot(root);
  reactRoot.render(
    <React.StrictMode>
      <Provider store={createConsoleStore()}>
        <Console config={config} />
      </Provider>
    </React.StrictMode>,
  );

  return {
    unmount: () => {
      reactRoot.unmount();
      styleEl.remove();
      root.textContent = '';
    },
  };
}
